import { getPeriodStart } from "./billingSchedule";
import { type Subscription } from "@/types/subscription";

export const getStatusVariant = (status: Subscription["status"]) => {
  switch (status) {
    case "active":
      return "success" as const;
    case "trial":
      return "warning" as const;
    case "cancelled":
      return "destructive" as const;
    case "paused":
      return "secondary" as const;
    default:
      return "outline" as const;
  }
};

export const getReconciliationIcon = (
  reconciliationStatus: Subscription["reconciliationStatus"],
) => {
  switch (reconciliationStatus) {
    case "paid":
      return "CheckCircle";
    case "cancelled":
      return "X";
    case "pending":
      return "Clock";
    default:
      return "AlertCircle";
  }
};

export const getReconciliationText = (subscription: Subscription) => {
  // Trial subscriptions
  if (subscription.status === "trial") {
    const trialEndDate = subscription.nextPayment;
    const now = new Date();

    if (trialEndDate > now) {
      return "Trial Active";
    } else {
      return subscription.reconciliationStatus === "pending"
        ? "Trial Ended"
        : "Trial Converted";
    }
  }

  // Cancelled subscriptions
  if (subscription.status === "cancelled") {
    return "Cancelled";
  }

  // Active subscriptions
  switch (subscription.reconciliationStatus) {
    case "paid":
      return "Up to Date";
    case "pending":
      return "Payment Due";
    case "cancelled":
      return "Cancelled";
    default:
      return "Status Unknown";
  }
};

export const getFrequencyText = (subscription: Subscription) => {
  // PAYG unscheduled → explicit label
  if (
    (subscription as any).billingType === "pay_as_you_go" &&
    !subscription.frequency
  ) {
    return "No fixed schedule";
  }
  switch (subscription.frequency) {
    case "weekly":
      return "/week";
    case "fortnightly":
      return "/fortnight";
    case "monthly":
      return "/month";
    case "every_2_months":
      return "/2 months";
    case "quarterly":
      return "/quarter";
    case "yearly":
      return "/year";
    case "custom":
      return `/${subscription.customFrequencyDays} days`;
    default:
      return "";
  }
};

export const getCategoryIcon = (category: Subscription["category"]) => {
  const categoryIcons: Record<string, string> = {
    Entertainment: "🎬",
    Music: "🎵",
    Productivity: "⚡",
    "Health & Fitness": "💪",
    "News & Media": "📰",
    "Cloud Storage": "☁️",
    Gaming: "🎮",
    Finance: "💳",
  };
  return categoryIcons[category] || "📱";
};

export const getSubscriptionStartDate = (sub: Subscription) => {
  return sub.startDate;
};

export const getCurrentPeriodStart = (sub: Subscription) => {
  if (!sub.nextPayment || !sub.frequency) {
    return sub.startDate;
  }
  return getPeriodStart(
    sub.nextPayment,
    sub.frequency,
    sub.customFrequencyDays,
  );
};

export const hasPaymentInCurrentPeriod = (sub: Subscription) => {
  if (!sub.nextPayment || !sub.frequency) return false;

  const periodEnd = new Date(sub.nextPayment);
  const periodStart = getPeriodStart(
    sub.nextPayment,
    sub.frequency,
    sub.customFrequencyDays,
  );

  return (sub.paymentHistory || []).some((p) => {
    const d = new Date(p.date);
    return p.status === "paid" && d >= periodStart && d < periodEnd;
  });
};
